import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { MaskedLines } from "./Reveal";
import { MODEL, HERO_IMAGE } from "../data";

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden"
    >
      {/* Full portrait — uncropped, natural colours, blended into the dark */}
      <div
        aria-hidden="true"
        className="absolute inset-y-0 right-0 h-full aspect-[3/4] max-w-full"
      >
        <img
          src={HERO_IMAGE}
          alt="Portrait of the model"
          className="h-full w-full object-cover object-top"
        />
        {/* soft blend of the left edge into the background (removes the hard seam) */}
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/10 to-transparent" />
        {/* gentle bottom fade for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-background/50 via-transparent to-transparent" />
      </div>

      {/* Content */}
      <motion.div
        style={reduce ? {} : { y: textY }}
        className="relative z-10 flex min-h-[100svh] flex-col justify-end px-6 lg:px-12 pt-28 pb-10"
      >
        <div className="mt-auto">
          <h1 className="font-serif font-light uppercase leading-[0.82] tracking-tight text-foreground text-[19vw] sm:text-[16vw] lg:text-[13vw]">
            <MaskedLines lines={[MODEL.firstName]} delay={0.5} className="block" />
            <MaskedLines
              lines={[MODEL.lastName]}
              delay={0.65}
              className="block italic text-accent"
            />
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 border-t border-border pt-6"
          >
            <p className="max-w-md text-sm sm:text-base font-light leading-relaxed tracking-wide text-muted-foreground">
              {MODEL.intro}
            </p>
            <a
              href="#work"
              data-testid="hero-cta"
              className="group inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-foreground"
            >
              View Work
              <span className="inline-block h-px w-10 bg-foreground transition-all duration-500 group-hover:w-16 group-hover:bg-accent" />
            </a>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
